import { useCallback, useEffect, useRef } from 'react'

export interface UseResizeObserverOptions {
    onResize?: (entries: ResizeObserverEntry[]) => void
    debounceMs?: number
    enabled?: boolean
}

export interface UseResizeObserverReturn {
    observeElement: (element: Element | null) => void
    unobserveElement: (element: Element | null) => void
    disconnect: () => void
}

export const useResizeObserver = ({
    onResize,
    debounceMs = 100,
    enabled = true
}: UseResizeObserverOptions = {}): UseResizeObserverReturn => {
    const observerRef = useRef<ResizeObserver>()
    const timeoutRef = useRef<NodeJS.Timeout>()
    const observedElementsRef = useRef<Set<Element>>(new Set())

    const debouncedCallback = useCallback((entries: ResizeObserverEntry[]) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            onResize?.(entries)
        }, debounceMs)
    }, [onResize, debounceMs])

    const observeElement = useCallback((element: Element | null) => {
        if (!element || !enabled) return

        if (!observerRef.current) {
            observerRef.current = new ResizeObserver(debouncedCallback)
        }

        if (!observedElementsRef.current.has(element)) {
            observerRef.current.observe(element)
            observedElementsRef.current.add(element)
        }
    }, [enabled, debouncedCallback])

    const unobserveElement = useCallback((element: Element | null) => {
        if (!element || !observerRef.current) return

        observerRef.current.unobserve(element)
        observedElementsRef.current.delete(element)
    }, [])

    const disconnect = useCallback(() => {
        if (observerRef.current) {
            observerRef.current.disconnect()
            observedElementsRef.current.clear()
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
    }, [])

    useEffect(() => {
        return () => {
            disconnect()
        }
    }, [disconnect])

    return {
        observeElement,
        unobserveElement,
        disconnect
    }
}